import type { SupplierAdmissionFormData } from "../../AdmissionApplicationModal/config/types";
import dayjs from "dayjs";

// ==================== 类型定义 ====================

/**
 * 后端返回的注册人信息数据结构
 */
interface RegistrantInfo {
  registrantId?: number;
  supplierId?: number;
  surname?: string; // 姓氏
  name?: string; // 名字
  phone?: string; // 电话
  deptCode?: string; // 部门代码
  email?: string; // 邮箱
  status?: number; // 状态
}

/**
 * 后端返回的银行信息数据结构
 */
interface BankInfo {
  accountType?: string; // 账户类型
  country?: string; // 国家
  openBankName?: string; // 开户银行名称
  openBankNumber?: string; // 开户银行编号
  account?: string; // 账号
  swiftCode?: string; // SWIFT代码
  accountHolder?: string; // 账户持有人
  openBankInSap?: number; // 是否在SAP中开户
  [key: string]: any;
}

/**
 * 后端返回的档案信息数据结构
 */
interface ProfileInfo {
  fileType?: number | string; // 文件类型：数字(0-4)或标签字符串
  fileName?: string; // 文件名称
  fileUrl?: string; // 文件URL
  fileSize?: string; // 文件大小
  id?: string; // 文件ID
  showTip?: boolean; // 是否显示提示
  fileTip?: string; // 文件提示信息
  [key: string]: any;
}

/**
 * 后端返回的供应商详情数据结构
 */
interface SupplierDetailResponse {
  // 供应商基本信息
  id?: string | number;
  supplierUserId?: string | number; // 供应商用户ID
  insId?: string | number; // 流程实例ID（用于获取历史记录）
  supplierName?: string;
  supplierChinese?: string;
  supplierUsName?: string; // 供应商英文名称
  supplierCode?: string;
  supplierRegistration?: string;
  supplyInfo?: string;
  // 日期字段（后端返回的是分开的开始和结束日期，或直接返回 businessValidity）
  supplierStartDate?: string | number;
  supplierEndDate?: string | number;
  businessValidity?: string; // 后端直接返回的营业有效期字符串
  // 新增字段
  supplierType?: number; // 1: 生产商, 2: 代理商
  supplierSocialCreditCode?: string; // 统一社会信用代码/商业登记号
  manufacturerName?: string; // 生产商名称
  // 生产商清单
  manufacturerList?: Array<{
    supplierName?: string;
    [key: string]: any;
  }>;
  // 地址信息 - 直接使用表单中的字段名称
  supplierCountry?: string;
  supplierCity?: string;
  supplierPostalCode?: string;
  supplierStreet?: string;
  supplierLanguage?: string;
  // 注册人信息
  registrantList?: RegistrantInfo[];
  // 银行信息列表
  bankInfoList?: BankInfo[];
  // 档案信息列表
  profileList?: ProfileInfo[];
  // 流程单号（编辑时从后端返回）
  processCode?: string;
  // 准入申请状态（用于判断是否为草稿）
  admissionApplicationStatus?: string;
  // 任务ID（草稿状态下用于调用 /view/pass 接口）
  taskId?: string | number;
  // 第二步的供应商设置信息
  factoryValue?: string;
  materialCategoryValue?: string;
  materialTwoCategoryValue?: string;
  materialTwoCategoryName?: string;
  materialPurposeValue?: string;
  materialPurposeName?: string;
  isGmpValue?: string;
  purchaseTypeValue?: string;
  supplierLevelValue?: string;
  // 第二步的采购信息
  purchaseGroupValue?: string;
  currencyValue?: string;
  paymentMethodValue?: string;
  paymentTermsValue?: string;
  internationalTradeTermsValue?: string;
  shoppingName?: string;
  // 第二步的财务信息
  accountGroupName?: string;
  accountGroupValue?: string;
  accountCode?: string;
  // 第二步的其他合作信息
  oaAddress?: string;
  oaAddressCode?: string;
  piInvoiceParty?: string;
  piInvoicePartyCode?: string;
  // 其他字段...
  [key: string]: any;
}

// ==================== 转换函数 ====================

/**
 * 语言代码映射：从数字代码转换为语言代码字符串
 * API 可能返回数字代码或直接返回语言代码字符串
 */
const LANGUAGE_CODE_MAP: Record<number | string, string> = {
  1: "zh_CN", // 简体中文
  2: "zh_HK", // 繁体中文
  3: "en_US", // English
  4: "other", // 其它语言
  // 直接支持字符串格式（API 直接返回语言代码时）
  zh_CN: "zh_CN",
  zh_HK: "zh_HK",
  en_US: "en_US",
  other: "other",
};

/**
 * 从 manufacturerList 数组中提取 supplierName 并以逗号隔开
 * 用于"生产商名称"字段显示
 *
 * 示例：
 * - 输入: [{ supplierName: "供应商A" }, { supplierName: "供应商B" }]
 * - 输出: "供应商A,供应商B"
 *
 * @param manufacturerList 生产商清单数组
 * @returns 逗号隔开的供应商名称字符串
 */
const extractManufacturerNamesFromList = (
  manufacturerList?: Array<{ supplierName?: string; [key: string]: any }>,
): string => {
  if (!Array.isArray(manufacturerList) || manufacturerList.length === 0) {
    return "";
  }

  return manufacturerList
    .map((item) => item.supplierName)
    .filter((name) => name) // 过滤掉空值
    .join(",");
};

/**
 * 将后端供应商详情数据转换为表单数据
 * 主要处理：
 * 1. 字段名称映射（如 supplierChinese -> supplierName）
 * 2. 日期范围转换（supplierStartDate + supplierEndDate -> businessValidity 字符串）
 * 3. 日期格式处理（时间戳/字符串 -> YYYY-MM-DD ~ YYYY-MM-DD 格式字符串）
 *
 * 注意：registrantList、bankInfoList、profileList 数组和 processCode 会直接传递给 Modal，由 Modal 自己决定如何处理
 *
 * @param apiData 后端返回的原始数据
 * @returns 转换后的表单数据（包含 processCode、registrantList、bankInfoList 和 profileList）
 */
export const transformSupplierDataToFormData = (
  apiData: SupplierDetailResponse,
): Partial<SupplierAdmissionFormData> & {
  id?: string | number;
  supplierUserId?: string | number;
  insId?: string | number;
  processCode?: string;
  registrantList?: RegistrantInfo[];
  bankInfoList?: BankInfo[];
  profileList?: ProfileInfo[];
  admissionApplicationStatus?: string;
  taskId?: string | number;
} => {
  if (!apiData) {
    return {};
  }

  // 处理营业有效期
  // 优先使用 API 直接返回的 businessValidity 字段
  // 如果没有，则从 supplierStartDate 和 supplierEndDate 转换
  const businessPeriodStr =
    apiData.businessValidity ||
    formatDateRangeToString(apiData.supplierStartDate, apiData.supplierEndDate);

  // 处理生产商名称：如果有 manufacturerList，则从中提取；否则使用原值
  const manufacturerNameStr =
    extractManufacturerNamesFromList(apiData.manufacturerList) ||
    apiData.manufacturerName ||
    "";

  return {
    // 供应商ID - 用于后续API调用
    id: apiData.id,

    // 供应商用户ID - 用于准入申请时提交
    supplierUserId: apiData.supplierUserId,

    // 流程实例ID - 用于获取历史记录
    insId: apiData.insId,

    // 流程单号（编辑时从后端返回，用于区分新增vs编辑）
    processCode: apiData.processCode,

    // 准入申请状态（用于判断是否为草稿状态）
    admissionApplicationStatus: apiData.admissionApplicationStatus,

    // 任务ID（草稿状态下用于调用 /view/pass 接口）
    taskId: apiData.taskId,

    // 供应商基本信息 - 字段名称映射
    supplierCode: apiData.supplierCode || "",
    supplierName: apiData.supplierChinese || apiData.supplierName || "",
    supplierUsName: apiData.supplierUsName || "", // 供应商英文名称
    supplierRegistration: apiData.supplierRegistration || "",
    supplyInfo: apiData.supplyInfo || "",
    businessValidity: businessPeriodStr,
    // 新增字段
    // 确保 supplierType 是数字类型（API 可能返回字符串）
    supplierType: apiData.supplierType
      ? Number(apiData.supplierType)
      : undefined,
    supplierSocialCreditCode: apiData.supplierSocialCreditCode || "",
    manufacturerName: manufacturerNameStr,

    // 地址信息 - 直接映射表单中的字段名称
    supplierCountry: apiData.supplierCountry || "",
    supplierCity: apiData.supplierCity || "",
    supplierPostalCode: apiData.supplierPostalCode || "",
    supplierStreet: apiData.supplierStreet || "",
    // 语言字段：转换数字代码为语言代码字符串
    supplierLanguage: apiData.supplierLanguage
      ? LANGUAGE_CODE_MAP[apiData.supplierLanguage]
      : undefined,

    // 注册人信息 - 直接传递整个数组，由 Modal 自己处理
    registrantList: apiData.registrantList,

    // 银行信息列表 - 直接传递整个数组，由 Modal 自己处理
    bankInfoList: apiData.bankInfoList,

    // 档案信息列表 - 直接传递整个数组，由 Modal 自己处理
    profileList: apiData.profileList,

    // 第二步的供应商设置信息
    factoryValue: apiData.factoryValue,
    materialCategoryValue: apiData.materialCategoryValue,
    materialTwoCategoryValue: apiData.materialTwoCategoryValue,
    materialTwoCategoryName: apiData.materialTwoCategoryName,
    materialPurposeValue: apiData.materialPurposeValue,
    materialPurposeName: apiData.materialPurposeName,
    isGmpValue: apiData.isGmpValue,
    purchaseTypeValue: apiData.purchaseTypeValue,
    supplierLevelValue: apiData.supplierLevelValue,

    // 第二步的采购信息
    purchaseGroupValue: apiData.purchaseGroupValue,
    currencyValue: apiData.currencyValue,
    paymentMethodValue: apiData.paymentMethodValue,
    paymentTermsValue: apiData.paymentTermsValue,
    internationalTradeTermsValue: apiData.internationalTradeTermsValue,
    shoppingName: apiData.shoppingName,

    // 第二步的财务信息
    accountGroupName: apiData.accountGroupName,
    accountGroupValue: apiData.accountGroupValue,
    accountCode: apiData.accountCode,

    // 第二步的其他合作信息
    oaAddress: apiData.oaAddress,
    oaAddressCode: apiData.oaAddressCode,
    piInvoiceParty: apiData.piInvoiceParty,
    piInvoicePartyCode: apiData.piInvoicePartyCode,
  };
};

/**
 * 将开始日期和结束日期转换为字符串格式显示
 * 支持多种日期格式：时间戳、ISO字符串、YYYY-MM-DD格式等
 *
 * 示例：
 * - 输入: startDate="2023-01-01", endDate="2025-12-31"
 * - 输出: "2023-01-01 ~ 2025-12-31"
 *
 * @param startDate 开始日期（时间戳或字符串）
 * @param endDate 结束日期（时间戳或字符串）
 * @returns 格式化的日期范围字符串，如果日期无效则返回空字符串
 */
const formatDateRangeToString = (
  startDate?: string | number,
  endDate?: string | number,
): string => {
  // 如果两个日期都不存在，返回空字符串
  if (!startDate || !endDate) {
    return "";
  }

  try {
    // 使用 dayjs 解析日期
    const startDayjs = dayjs(startDate);
    const endDayjs = dayjs(endDate);

    // 验证日期是否有效
    if (!startDayjs.isValid() || !endDayjs.isValid()) {
      return "";
    }

    // 格式化为 YYYY-MM-DD ~ YYYY-MM-DD
    return `${startDayjs.format("YYYY-MM-DD")} ~ ${endDayjs.format("YYYY-MM-DD")}`;
  } catch (error) {
    return "";
  }
};

/**
 * 从表单数据中提取日期范围字符串，转换为后端所需的格式
 * 用于表单提交时使用
 *
 * 示例：
 * - 输入: businessValidity="2023-01-01 ~ 2025-12-31"
 * - 输出: { supplierStartDate: "2023-01-01", supplierEndDate: "2025-12-31" }
 *
 * @param formData 表单提交的数据
 * @returns 包含 supplierStartDate 和 supplierEndDate 的对象
 */
export const extractDateRangeFromFormData = (
  formData: Partial<SupplierAdmissionFormData>,
): { supplierStartDate?: string; supplierEndDate?: string } => {
  const result: { supplierStartDate?: string; supplierEndDate?: string } = {};

  if (
    formData.businessValidity &&
    typeof formData.businessValidity === "string"
  ) {
    // 解析格式为 "YYYY-MM-DD ~ YYYY-MM-DD" 的字符串
    const parts = formData.businessValidity.split("~");

    if (parts.length === 2) {
      const startDate = parts[0].trim();
      const endDate = parts[1].trim();

      if (dayjs(startDate).isValid()) {
        result.supplierStartDate = startDate;
      }

      if (dayjs(endDate).isValid()) {
        result.supplierEndDate = endDate;
      }
    }
  }

  return result;
};
